<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("agent_commission.remittance_title") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <!-- Upload card -->
            <card class="mb-4">
                <template slot="header">
                    <h3 class="mb-0">{{ $t("agent_commission.upload") }}</h3>
                </template>

                <div class="row align-items-end">
                    <div class="col-md-4">
                        <base-input :label="$t('agent_commission.carrier')">
                            <el-select v-model="carrier" clearable class="w-100" :placeholder="$t('agent_commission.carrier')">
                                <el-option
                                    v-for="opt in carrierOptions"
                                    :key="opt.value"
                                    :label="opt.label"
                                    :value="opt.value"
                                ></el-option>
                            </el-select>
                        </base-input>
                    </div>
                    <div class="col-md-5">
                        <base-input :label="$t('agent_commission.upload')">
                            <input
                                ref="fileInput"
                                type="file"
                                accept=".xls,.xlsx"
                                class="form-control"
                                @change="onFileChange"
                            />
                        </base-input>
                    </div>
                    <div class="col-md-3 pb-3">
                        <base-button
                            type="primary"
                            :loading="uploading"
                            :disabled="!selectedFile || importRunning"
                            @click="doUpload"
                        >
                            <i class="fa fa-upload mr-1"></i>{{ $t("agent_commission.upload") }}
                        </base-button>
                    </div>
                </div>
                <small class="text-muted d-block">{{ $t("agent_commission.file_hint_xls") }}</small>

                <!-- Import progress -->
                <div v-if="importState" class="mt-4">
                    <div class="progress mb-3" style="height: 24px;">
                        <div
                            class="progress-bar bg-success"
                            role="progressbar"
                            :style="{ width: importPercent + '%' }"
                        >
                            {{ importPercent }}%
                        </div>
                    </div>
                    <div class="row text-center">
                        <div class="col">
                            <small class="text-muted d-block">{{ $t("agent_commission.remittance.total") }}</small>
                            <h4>{{ importState.total || 0 }}</h4>
                        </div>
                        <div class="col">
                            <small class="text-muted d-block">{{ $t("agent_commission.remittance.processing") }}</small>
                            <h4>{{ importState.processed || 0 }}</h4>
                        </div>
                        <div class="col">
                            <small class="text-muted d-block text-success">{{ $t("agent_commission.remittance.matched") }}</small>
                            <h4 class="text-success">{{ importState.matched || 0 }}</h4>
                        </div>
                        <div class="col">
                            <small class="text-muted d-block text-warning">{{ $t("agent_commission.remittance.unmatched") }}</small>
                            <h4 class="text-warning">{{ importState.unmatched || 0 }}</h4>
                        </div>
                        <div class="col">
                            <small class="text-muted d-block text-secondary">{{ $t("agent_commission.remittance.duplicates_skipped") }}</small>
                            <h4 class="text-secondary">{{ importState.duplicates || importState.skipped || 0 }}</h4>
                        </div>
                        <div class="col">
                            <small class="text-muted d-block text-danger">{{ $t("agent_commission.remittance.failed") }}</small>
                            <h4 class="text-danger">{{ importState.fail || 0 }}</h4>
                        </div>
                    </div>
                    <div v-if="importState.running" class="text-muted mt-2 text-center">
                        <i class="fa fa-spinner fa-spin"></i> {{ $t("agent_commission.remittance.processing") }}
                    </div>
                    <div v-else class="text-success mt-2 text-center">
                        <i class="fa fa-check-circle"></i> {{ $t("agent_commission.remittance.done") }}
                    </div>
                    <div v-if="importState.errors && importState.errors.length > 0" class="mt-3">
                        <ul class="list-unstyled small text-danger mb-0" style="max-height: 140px; overflow-y: auto;">
                            <li v-for="(e, idx) in importState.errors" :key="idx">{{ e.message || e }}</li>
                        </ul>
                    </div>
                </div>
            </card>

            <!-- Unmatched rows -->
            <card class="mb-4">
                <template slot="header">
                    <div class="row align-items-center">
                        <div class="col">
                            <h3 class="mb-0">{{ $t("agent_commission.remittance.unmatched") }}</h3>
                        </div>
                        <div class="col text-right">
                            <base-button type="secondary" size="sm" :loading="unmatchedLoading" @click="loadUnmatched">
                                <i class="fa fa-sync"></i>
                            </base-button>
                        </div>
                    </div>
                </template>

                <div v-if="unmatched.length === 0" class="text-center text-muted py-4">
                    {{ $t("agent_commission.remittance.no_unmatched") }}
                </div>
                <el-table v-else :data="unmatched" header-row-class-name="thead-light" v-loading="unmatchedLoading">
                    <el-table-column :label="$t('agent_commission.remittance.tracking')" prop="tracking_number"></el-table-column>
                    <el-table-column :label="$t('agent_commission.carrier')" prop="carrier"></el-table-column>
                    <el-table-column :label="$t('agent_commission.remittance.cod_amount')">
                        <div slot-scope="{ row }">{{ formatAmount(row.cod_amount) }}</div>
                    </el-table-column>
                    <el-table-column :label="$t('agent_commission.remittance.transfer_amount')">
                        <div slot-scope="{ row }">{{ formatAmount(row.transfer_amount) }}</div>
                    </el-table-column>
                    <el-table-column :label="$t('agent_commission.remittance.transfer_date')">
                        <div slot-scope="{ row }">{{ formatDate(row.transfer_date) }}</div>
                    </el-table-column>
                    <el-table-column :label="$t('actions')" align="right" min-width="120">
                        <div slot-scope="{ row }">
                            <base-button type="primary" size="sm" @click="openMatch(row)">
                                {{ $t("agent_commission.remittance.match") }}
                            </base-button>
                        </div>
                    </el-table-column>
                </el-table>
            </card>

            <!-- Batch history -->
            <card>
                <template slot="header">
                    <h3 class="mb-0">{{ $t("agent_commission.remittance.history") }}</h3>
                </template>
                <div v-if="batches.length === 0" class="text-center text-muted py-4">
                    {{ $t("agent_commission.remittance.no_batches") }}
                </div>
                <el-table v-else :data="batches" header-row-class-name="thead-light">
                    <el-table-column :label="$t('agent_commission.remittance.file_name')" prop="file_name"></el-table-column>
                    <el-table-column :label="$t('agent_commission.carrier')" prop="carrier"></el-table-column>
                    <el-table-column :label="$t('agent_commission.remittance.total')" prop="row_count"></el-table-column>
                    <el-table-column :label="$t('agent_commission.remittance.matched')" prop="matched_count"></el-table-column>
                    <el-table-column :label="$t('agent_commission.remittance.unmatched')" prop="unmatched_count"></el-table-column>
                    <el-table-column :label="$t('agent_commission.round.status')">
                        <div slot-scope="{ row }">
                            <span class="badge" :class="statusBadgeClass(row.status)">{{ row.status }}</span>
                        </div>
                    </el-table-column>
                    <el-table-column :label="$t('agent_commission.round.created_at')">
                        <div slot-scope="{ row }">{{ formatDate(row.created_at) }}</div>
                    </el-table-column>
                </el-table>
            </card>
        </div>

        <!-- Manual match dialog -->
        <modal :show.sync="matchModalShow" modal-classes="modal-secondary">
            <h3 slot="header" class="modal-title">
                {{ $t("agent_commission.remittance.match_dialog_title") }}
            </h3>
            <div v-if="matchRow">
                <p class="small text-muted mb-3">
                    {{ $t("agent_commission.remittance.tracking") }}: <code>{{ matchRow.tracking_number }}</code>
                    <br />
                    {{ $t("agent_commission.remittance.cod_amount") }}: {{ formatAmount(matchRow.cod_amount) }}
                    &nbsp;|&nbsp;
                    {{ $t("agent_commission.remittance.transfer_date") }}: {{ formatDate(matchRow.transfer_date) }}
                </p>
                <base-input :label="$t('agent_commission.remittance.select_order')">
                    <el-input v-model="matchOrderId" :placeholder="$t('agent_commission.remittance.select_order')"></el-input>
                </base-input>
            </div>
            <template slot="footer">
                <base-button type="link" @click="matchModalShow = false">
                    {{ $t("cancel") }}
                </base-button>
                <base-button type="primary" :loading="matching" :disabled="!matchOrderId" @click="doMatch">
                    {{ $t("agent_commission.remittance.match") }}
                </base-button>
            </template>
        </modal>
    </div>
</template>

<script>
import { Table, TableColumn, Select, Option, Input } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import Modal from "@/components/Modal";
import { mapGetters, mapActions } from "vuex";
import swal from "sweetalert2";

export default {
    name: "AgentCommissionRemittance",
    components: {
        RouteBreadCrumb,
        Modal,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Select.name]: Select,
        [Option.name]: Option,
        [Input.name]: Input,
    },
    data() {
        return {
            carrier: "",
            carrierOptions: [
                { value: "thaipost", label: "ไปรษณีย์ไทย (Thaipost)" },
                { value: "flash", label: "Flash" },
                { value: "kerry", label: "Kerry" },
                { value: "jt", label: "J&T" },
                { value: "spx", label: "SPX" },
                { value: "ninjavan", label: "NinjaVan" },
            ],
            selectedFile: null,
            uploading: false,
            importState: null,
            importPollTimer: null,
            unmatchedLoading: false,
            matchModalShow: false,
            matchRow: null,
            matchOrderId: "",
            matching: false,
        };
    },
    computed: {
        ...mapGetters({
            batches: "AgentCommission/getRemittanceBatches",
            unmatched: "AgentCommission/getRemittanceUnmatched",
        }),
        importRunning() {
            return !!(this.importState && this.importState.running);
        },
        importPercent() {
            if (!this.importState || !this.importState.total) return this.importState && this.importState.running ? 5 : 0;
            return Math.min(100, Math.round((this.importState.processed / this.importState.total) * 100));
        },
    },
    mounted() {
        this.loadBatches();
        this.loadUnmatched();
        this.checkExistingImport();
    },
    beforeDestroy() {
        this.stopImportPolling();
    },
    methods: {
        ...mapActions({
            importRemittance: "AgentCommission/importRemittance",
            remittanceImportStatus: "AgentCommission/remittanceImportStatus",
            fetchRemittanceBatches: "AgentCommission/fetchRemittanceBatches",
            fetchRemittanceUnmatched: "AgentCommission/fetchRemittanceUnmatched",
            matchRemittanceRow: "AgentCommission/matchRemittanceRow",
        }),
        async loadBatches() {
            try {
                await this.fetchRemittanceBatches();
            } catch (err) {
                // handled in module
            }
        },
        async loadUnmatched() {
            this.unmatchedLoading = true;
            try {
                await this.fetchRemittanceUnmatched();
            } catch (err) {
                // handled in module
            } finally {
                this.unmatchedLoading = false;
            }
        },
        onFileChange(e) {
            const files = e.target.files;
            this.selectedFile = files && files.length > 0 ? files[0] : null;
        },
        async doUpload() {
            if (!this.selectedFile) return;
            this.uploading = true;
            try {
                const resp = await this.importRemittance({ file: this.selectedFile, carrier: this.carrier });
                const datum = (resp && resp.data && (resp.data.Datum || (resp.data.Data && resp.data.Data[0]))) || null;
                this.importState = datum || { running: true, total: 0, processed: 0, matched: 0, unmatched: 0 };
                this.startImportPolling();
                // reset file input
                this.selectedFile = null;
                if (this.$refs.fileInput) this.$refs.fileInput.value = "";
            } catch (err) {
                this.showError(err);
            } finally {
                this.uploading = false;
            }
        },
        async checkExistingImport() {
            try {
                const resp = await this.remittanceImportStatus();
                const state = resp && resp.data && (resp.data.Datum || (resp.data.Data && resp.data.Data[0]));
                if (state && state.running) {
                    this.importState = state;
                    this.startImportPolling();
                }
            } catch (e) {
                // idle is fine
            }
        },
        startImportPolling() {
            this.stopImportPolling();
            const self = this;
            this.importPollTimer = setInterval(async function() {
                try {
                    const resp = await self.remittanceImportStatus();
                    const state = resp && resp.data && (resp.data.Datum || (resp.data.Data && resp.data.Data[0]));
                    if (state) {
                        self.importState = state;
                        if (!state.running) {
                            self.stopImportPolling();
                            self.loadBatches();
                            self.loadUnmatched();
                        }
                    }
                } catch (e) {
                    self.stopImportPolling();
                }
            }, 2000);
        },
        stopImportPolling() {
            if (this.importPollTimer) {
                clearInterval(this.importPollTimer);
                this.importPollTimer = null;
            }
        },
        openMatch(row) {
            this.matchRow = row;
            this.matchOrderId = "";
            this.matchModalShow = true;
        },
        async doMatch() {
            if (!this.matchOrderId) return;
            this.matching = true;
            try {
                await this.matchRemittanceRow({ rowId: this.matchRow.id, boOrderId: Number(this.matchOrderId) });
                swal.fire({
                    title: this.$t("agent_commission.remittance.match_success"),
                    icon: "success",
                    confirmButtonClass: "btn btn-success btn-fill",
                    buttonsStyling: false,
                });
                this.matchModalShow = false;
                this.loadUnmatched();
                this.loadBatches();
            } catch (err) {
                this.showError(err);
            } finally {
                this.matching = false;
            }
        },
        statusBadgeClass(status) {
            if (status === "done") return "badge-success";
            if (status === "failed") return "badge-danger";
            return "badge-warning";
        },
        showError(err) {
            const msg = err.response && err.response.data && err.response.data.Cause
                ? err.response.data.Cause
                : err.message;
            swal.fire({ title: msg, icon: "error", confirmButtonClass: "btn btn-danger btn-fill", buttonsStyling: false });
        },
        formatDate(val) {
            if (!val) return "-";
            return String(val).replace("T", " ").substring(0, 16);
        },
        formatAmount(val) {
            if (val == null) return "-";
            return Number(val).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
    },
};
</script>
